import { render, screen, waitFor, within } from '@testing-library/react';
import { CategoryProvider } from '../context/CategoryContext.jsx';
import { CATEGORY_TYPES } from '../config/categoryTypes';
import { getCategoryConfig } from '../config/categoryConfigs';

/**
 * Testing utilities for category system
 */

// Mock data generators
export const createMockCategoryConfig = (overrides = {}) => ({
  id: 'test-category',
  type: CATEGORY_TYPES.WEDDING,
  name: 'testCategory',
  displayName: 'Test Category',
  description: 'Test category description',
  icon: '🧪',
  image: '/test-image.jpg',
  features: ['BOOKING', 'CUSTOMIZATION'],
  status: 'ACTIVE',
  metadata: {
    priority: 1,
    popularityScore: 85,
    dataSource: 'TEST_SERVICES',
    filterKey: 'test',
    pageConfigKey: 'test'
  },
  customization: {
    primaryColor: '#dc2626',
    secondaryColor: '#fecaca',
    theme: 'test',
    showAdvancedFilters: true,
    enablePriceComparison: true,
    showReviews: true
  },
  ...overrides
});

export const createMockService = (overrides = {}) => ({
  id: 'test-service-1',
  title: 'Test Service',
  description: 'Test service description',
  price: 1000,
  duration: '2 hours',
  rating: 4.5,
  reviewCount: 100,
  image: '🧪',
  features: ['BOOKING', 'CUSTOMIZATION'],
  category: 'test',
  ...overrides
});

export const createMockServices = (count = 5) => {
  return Array.from({ length: count }, (_, index) => 
    createMockService({
      id: `test-service-${index + 1}`,
      title: `Test Service ${index + 1}`,
      price: 500 + (index * 200)
    })
  );
};

// Custom render function with providers
export const renderWithCategoryProvider = (ui, options = {}) => {
  const Wrapper = ({ children }) => (
    <CategoryProvider>
      {children}
    </CategoryProvider>
  );

  return render(ui, { wrapper: Wrapper, ...options });
};

// Test helpers
export const categoryTestHelpers = {
  // Wait for category to load
  waitForCategoryLoad: async (categoryName) => {
    await waitFor(() => {
      expect(screen.getByText(new RegExp(categoryName, 'i'))).toBeInTheDocument();
    });
  },

  // Wait for services to load
  waitForServicesLoad: async (expectedCount = 1) => {
    await waitFor(() => {
      const services = screen.getAllByTestId(/service-card/);
      expect(services).toHaveLength(expectedCount);
    });
  },

  // Search for services
  searchServices: async (user, query) => {
    const searchInput = screen.getByPlaceholderText(/search/i);
    await user.clear(searchInput);
    await user.type(searchInput, query);
  },

  // Apply filter
  applyFilter: async (user, filterType, value) => {
    const filter = screen.getByLabelText(new RegExp(filterType, 'i'));
    if (filter.type === 'checkbox') {
      await user.click(filter);
    } else if (filter.type === 'select') {
      await user.selectOptions(filter, value);
    } else {
      await user.clear(filter);
      await user.type(filter, value);
    }
  },

  // Click service action button
  clickServiceButton: async (user, serviceTitle, buttonText) => {
    const service = screen.getByText(serviceTitle).closest('[data-testid*="service-card"]');
    const button = within(service).getByText(new RegExp(buttonText, 'i'));
    await user.click(button);
  },

  // Toggle favorite
  toggleFavorite: async (user, serviceTitle) => {
    const service = screen.getByText(serviceTitle).closest('[data-testid*="service-card"]');
    const favoriteButton = within(service).getByLabelText(/favorite/i);
    await user.click(favoriteButton);
  },

  // Change view mode
  changeViewMode: async (user, mode) => {
    const viewModeButton = screen.getByLabelText(new RegExp(`${mode} view`, 'i'));
    await user.click(viewModeButton);
  }
};

// Mock API responses
export const mockApiResponses = {
  getCategoryServices: (categoryType, count = 5) => ({
    data: createMockServices(count),
    total: count,
    page: 1,
    pageSize: 10
  }),

  searchServices: (query, services) => ({
    data: services.filter(s => 
      s.title.toLowerCase().includes(query.toLowerCase()) ||
      s.description.toLowerCase().includes(query.toLowerCase())
    ),
    query,
    total: services.length
  }),

  filterServices: (filters, services) => ({
    data: services.filter(service => {
      let matches = true;
      
      if (filters.priceRange) {
        matches = matches && 
          service.price >= filters.priceRange.min &&
          service.price <= filters.priceRange.max;
      }
      
      if (filters.rating) {
        matches = matches && service.rating >= filters.rating;
      }
      
      if (filters.features && filters.features.length > 0) {
        matches = matches && 
          filters.features.every(feature => service.features.includes(feature));
      }
      
      return matches;
    }),
    filters,
    total: services.length
  })
};

// Test scenarios
export const testScenarios = {
  // Happy path: category loads successfully
  categoryLoadsSuccessfully: async (categoryType) => {
    const config = getCategoryConfig(categoryType);
    expect(config).toBeDefined();
    expect(config.displayName).toBeTruthy();
    expect(config.description).toBeTruthy();
  },

  // Error handling: invalid category
  invalidCategoryHandling: async (invalidCategoryType) => {
    const config = getCategoryConfig(invalidCategoryType);
    expect(config).toBeNull();
  },

  // Search functionality
  searchFunctionality: async (user, services, query) => {
    await categoryTestHelpers.searchServices(user, query);
    
    await waitFor(() => {
      const expectedResults = services.filter(s => 
        s.title.toLowerCase().includes(query.toLowerCase())
      );
      expectedResults.forEach(service => {
        expect(screen.getByText(service.title)).toBeInTheDocument();
      });
    });
  },

  // Filter functionality
  filterFunctionality: async (user, filters) => {
    for (const [filterType, value] of Object.entries(filters)) {
      await categoryTestHelpers.applyFilter(user, filterType, value);
    }
    
    // Verify filtered results
    await waitFor(() => {
      // Results should be filtered based on applied filters
      const serviceCards = screen.getAllByTestId(/service-card/);
      expect(serviceCards.length).toBeGreaterThan(0);
    });
  },

  // Service interaction
  serviceInteraction: async (user, serviceName, action) => {
    await categoryTestHelpers.clickServiceButton(user, serviceName, action);
    
    // Verify action was triggered
    if (action.toLowerCase() === 'book now') {
      // Should navigate to booking page or show booking modal
      await waitFor(() => {
        expect(screen.getByText(/booking/i)).toBeInTheDocument();
      });
    }
  }
};

// Performance testing utilities
export const performanceTestUtils = {
  measureRenderTime: async (Component, props = {}) => {
    const startTime = performance.now();
    render(<Component {...props} />);
    const endTime = performance.now();
    return endTime - startTime;
  },

  measureLoadTime: async (categoryType) => {
    const startTime = performance.now();
    const config = getCategoryConfig(categoryType);
    const endTime = performance.now();
    return { config, loadTime: endTime - startTime };
  },

  benchmarkFiltering: (services, filters, iterations = 1000) => {
    const startTime = performance.now();
    
    for (let i = 0; i < iterations; i++) {
      mockApiResponses.filterServices(filters, services);
    }
    
    const endTime = performance.now();
    return (endTime - startTime) / iterations;
  }
};

// Accessibility testing helpers
export const a11yTestHelpers = {
  checkKeyboardNavigation: async (user) => {
    // Test tab navigation
    await user.tab();
    expect(document.activeElement).toHaveAttribute('tabindex');
  },

  checkAriaLabels: () => {
    const buttons = screen.getAllByRole('button');
    buttons.forEach(button => {
      expect(button).toHaveAttribute('aria-label');
    });
  },

  checkColorContrast: (element) => {
    const styles = getComputedStyle(element);
    const backgroundColor = styles.backgroundColor;
    const color = styles.color;
    
    // Basic contrast check (in real implementation, use a library)
    expect(backgroundColor).toBeTruthy();
    expect(color).toBeTruthy();
  },

  checkFocusManagement: async (user) => {
    const focusableElements = screen.getAllByRole('button');
    for (const element of focusableElements) {
      element.focus();
      expect(document.activeElement).toBe(element);
    }
  }
};

// Integration test helpers
export const integrationTestHelpers = {
  simulateUserJourney: async (user, journey) => {
    for (const step of journey) {
      switch (step.action) {
        case 'navigate':
          // Simulate navigation
          break;
        case 'search':
          await categoryTestHelpers.searchServices(user, step.query);
          break;
        case 'filter':
          await categoryTestHelpers.applyFilter(user, step.filterType, step.value);
          break;
        case 'interact':
          await categoryTestHelpers.clickServiceButton(user, step.service, step.button);
          break;
        default:
          break;
      }
    }
  },

  verifyAnalyticsEvents: (expectedEvents) => {
    // Mock analytics verification
    expectedEvents.forEach(event => {
      expect(console.log).toHaveBeenCalledWith(
        expect.stringContaining('📊 Analytics Event:'),
        expect.objectContaining({ eventName: event })
      );
    });
  }
};
